import { useCallback } from 'react';
import { useMessagingService } from '../../messaging/useMessagingService';
import {
  MessageListResponse,
  ConversationListResponse,
  CreateMessagePayload,
  BroadcastMessagePayload,
  MessageWithRelations,
  MessageQueryParams,
  MessageType,
  TypingIndicatorPayload,
} from '../../messaging/messaging.interfaces';

/**
 * Guardian Messaging Service
 * Wraps the main messaging service but filters messages by studentId (selected child)
 * Maintains the same RabbitMQ/WebSocket structure and flow
 * 
 * Backend Expectations:
 * - Backend should filter messages where studentId is involved (as sender or receiver for direct messages)
 * - For direct messages: Return messages where studentId is either sender_id or receiver_id
 * - For broadcasts: Return messages where class_id matches student's class
 * - WebSocket events should be filtered server-side based on studentId
 * - All API calls should include studentId parameter for filtering
 */
export const useGuardianMessagingService = (studentId: string) => {
  const messagingService = useMessagingService();

  // Get all messages for the student (direct messages where student is receiver, or broadcasts to student's class)
  const getAllMessages = useCallback(
    async (
      params?: MessageQueryParams
    ): Promise<MessageListResponse> => {
      // Filter by receiver_id = studentId for direct messages where student is the receiver
      // For broadcasts, backend should filter by student's class_id based on studentId
      // Backend API should accept studentId parameter to filter messages
      const queryParams: MessageQueryParams = {
        ...params,
        receiver_id: studentId, // Filter direct messages where student is receiver
        // Note: Backend should handle class_id filtering for broadcasts based on studentId
      };

      return messagingService.getAllMessages(queryParams);
    },
    [messagingService, studentId]
  );

  // Get direct messages between student and another user
  const getDirectMessages = useCallback(
    async (
      otherUserId: string, // The other party in the conversation
      params?: { page?: number; limit?: number }
    ): Promise<MessageListResponse> => {
      // Get messages where:
      // 1. sender_id = studentId AND receiver_id = otherUserId, OR
      // 2. sender_id = otherUserId AND receiver_id = studentId
      // Backend should handle this filtering based on studentId
      // For now, we query messages where student is receiver and otherUser is sender
      return messagingService.getDirectMessages(studentId, params);
    },
    [messagingService, studentId]
  );

  // Get broadcast messages for the student's class
  const getBroadcastMessages = useCallback(
    async (
      class_id?: string,
      params?: { page?: number; limit?: number }
    ): Promise<MessageListResponse> => {
      // Backend should filter broadcasts by student's class_id
      // class_id parameter here is optional override
      return messagingService.getBroadcastMessages(class_id, params);
    },
    [messagingService]
  );

  // Get conversations for the student
  const getConversations = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
    }): Promise<ConversationListResponse> => {
      // Backend should filter conversations where student is involved
      return messagingService.getConversations(params);
    },
    [messagingService]
  );

  // Send direct message (guardian replying on behalf of student)
  const sendDirectMessage = useCallback(
    async (
      payload: CreateMessagePayload & { sender_id?: string }
    ): Promise<MessageWithRelations> => {
      // For guardian, sender_id should be studentId (message sent on behalf of student)
      const guardianPayload: CreateMessagePayload & { sender_id?: string } = {
        ...payload,
        receiver_id: payload.receiver_id, // The recipient
        sender_id: studentId, // Student is the sender (guardian acting on behalf)
        type: MessageType.DIRECT_MESSAGE,
      };

      return messagingService.sendDirectMessage(guardianPayload);
    },
    [messagingService, studentId]
  );

  // Send broadcast message (guardian replying to class broadcast)
  const sendBroadcastMessage = useCallback(
    async (
      payload: BroadcastMessagePayload & { sender_id?: string }
    ): Promise<MessageWithRelations> => {
      // For guardian, sender_id should be studentId
      const guardianPayload: BroadcastMessagePayload & { sender_id?: string } = {
        ...payload,
        sender_id: studentId, // Student is the sender
        type: MessageType.BROADCAST,
      };

      return messagingService.sendBroadcastMessage(guardianPayload);
    },
    [messagingService, studentId]
  );

  // Mark messages as read
  const markMessagesAsRead = useCallback(
    async (
      message_ids: string[]
    ): Promise<{ success: boolean; updated_count: number }> => {
      return messagingService.markMessagesAsRead(message_ids);
    },
    [messagingService]
  );

  // WebSocket event listeners (same as main service)
  const onNewMessage = useCallback(
    (callback: (message: MessageWithRelations) => void) => {
      // Filter messages to only those relevant to the student
      return messagingService.onNewMessage((message) => {
        // Only call callback if message is for this student
        if (
          (message.type === MessageType.DIRECT_MESSAGE &&
            message.receiver_id === studentId) ||
          (message.type === MessageType.BROADCAST &&
            message.class_id) // Backend should filter broadcasts by student's class
        ) {
          callback(message);
        }
      });
    },
    [messagingService, studentId]
  );

  const onMessageSent = useCallback(
    (callback: (message: MessageWithRelations) => void) => {
      return messagingService.onMessageSent(callback);
    },
    [messagingService]
  );

  const onTyping = useCallback(
    (callback: (indicator: TypingIndicatorPayload) => void) => {
      return messagingService.onTyping(callback);
    },
    [messagingService]
  );

  const onStopTyping = useCallback(
    (callback: (indicator: TypingIndicatorPayload) => void) => {
      return messagingService.onStopTyping(callback);
    },
    [messagingService]
  );

  const sendTypingIndicator = useCallback(
    (payload: TypingIndicatorPayload) => {
      messagingService.sendTypingIndicator(payload);
    },
    [messagingService]
  );

  const sendStopTypingIndicator = useCallback(
    (payload: TypingIndicatorPayload) => {
      messagingService.sendStopTypingIndicator(payload);
    },
    [messagingService]
  );

  return {
    // Connection (same as main service)
    isConnected: messagingService.isConnected,
    onConnectionSuccess: messagingService.onConnectionSuccess,
    onError: messagingService.onError,

    // Messages (filtered by studentId)
    getAllMessages,
    getDirectMessages,
    getBroadcastMessages,
    sendDirectMessage,
    sendBroadcastMessage,
    onNewMessage,
    onMessageSent,
    markMessagesAsRead,

    // Conversations (filtered by studentId)
    getConversations,

    // Typing indicators (same as main service)
    onTyping,
    onStopTyping,
    sendTypingIndicator,
    sendStopTypingIndicator,
  };
};
